import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Monitor, Headphones, Wifi, Calendar, BookOpen, Users, Clock, Wrench, Shield, Zap, AlertTriangle, Lock, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON_MAP = { monitor: Monitor, headphones: Headphones, wifi: Wifi, calendar: Calendar, book: BookOpen, users: Users, clock: Clock, wrench: Wrench, shield: Shield, zap: Zap, alert: AlertTriangle };
const CAT_LABELS = { setup: 'Equipment Setup', onboarding: 'New Hire Onboarding', productivity: 'Productivity', troubleshooting: 'Troubleshooting', safety: 'Safety' };

export default function HelpHub() {
  const { user } = useAuth();
  const [guides, setGuides] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/help-hub`).then(r => { setGuides(r.data.guides); setLoading(false); }).catch(console.error);
  }, []);

  const categories = ['all', ...new Set(guides.map(g => g.category))];
  const filtered = filter === 'all' ? guides : guides.filter(g => g.category === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Help Hub</h1>
          <p className="text-[#1C2A24]/60 mt-2">Guides for equipment setup, onboarding, productivity, and common WFH questions</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="help-quick-links">
          <Link to="/tools" className="bg-white rounded-2xl border border-[#E5E0D8] p-4 text-center hover:border-[#047857]/30 transition-all">
            <Wrench size={20} className="text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs font-medium">Assessment Tools</p>
          </Link>
          <Link to="/equipment" className="bg-white rounded-2xl border border-[#E5E0D8] p-4 text-center hover:border-[#047857]/30 transition-all">
            <Monitor size={20} className="text-[#D97757] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs font-medium">Equipment Shop</p>
          </Link>
          <Link to="/webinars" className="bg-white rounded-2xl border border-[#E5E0D8] p-4 text-center hover:border-[#047857]/30 transition-all">
            <Calendar size={20} className="text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs font-medium">Live Webinars</p>
          </Link>
          <Link to="/support" className="bg-white rounded-2xl border border-[#E5E0D8] p-4 text-center hover:border-[#047857]/30 transition-all">
            <Users size={20} className="text-[#86A789] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs font-medium">Contact Support</p>
          </Link>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="help-filters">
          {categories.map(cat => (
            <button key={cat} onClick={() => { setFilter(cat); setSelected(null); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === cat ? 'bg-[#047857] text-white' : 'bg-[#F4F1E8] text-[#1C2A24]/70 hover:bg-[#E5E0D8]'}`}>
              {cat === 'all' ? 'All Guides' : CAT_LABELS[cat] || cat}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Guide List */}
          <div className="md:col-span-1 space-y-2" data-testid="guide-list">
            {loading ? [1,2,3,4].map(i => <div key={i} className="h-16 bg-white rounded-2xl animate-pulse" />) :
              filtered.map(guide => {
                const Icon = ICON_MAP[guide.icon] || BookOpen;
                return (
                  <div key={guide.id} onClick={() => setSelected(guide)}
                    className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all ${selected?.id === guide.id ? 'border-[#047857] shadow-md' : 'border-[#E5E0D8] hover:border-[#047857]/30'}`}
                    data-testid={`guide-${guide.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#A7F3D0]/30 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-[#047857]" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{guide.title}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[9px] text-[#1C2A24]/40 capitalize">{guide.category}</span>
                          {guide.locked && <Lock size={10} className="text-[#D97757]" strokeWidth={1.5} />}
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-[#E5E0D8] shrink-0" strokeWidth={1.5} />
                    </div>
                  </div>
                );
              })
            }
          </div>

          {/* Guide Content */}
          <div className="md:col-span-2">
            {!selected ? (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center">
                <BookOpen size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#1C2A24]/50">Select a guide to read</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="guide-content">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[10px] text-[#047857] bg-[#A7F3D0]/30 px-2 py-0.5 rounded-full capitalize">{selected.category}</span>
                  {selected.tier !== 'free' && <span className="text-[10px] text-[#D97757] bg-[#D97757]/10 px-2 py-0.5 rounded-full capitalize">{selected.tier} Guide</span>}
                </div>
                <h2 className="text-2xl font-medium mb-4" style={{fontFamily:'Outfit'}}>{selected.title}</h2>
                <div className="prose prose-sm max-w-none">
                  <p className="text-[#1C2A24]/70 leading-relaxed whitespace-pre-line">{selected.content}</p>
                </div>
                {selected.locked && (
                  <div className="mt-6 bg-[#D97757]/10 rounded-2xl p-5 border border-[#D97757]/20 text-center" data-testid="upgrade-prompt">
                    <Lock size={24} className="text-[#D97757] mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-[#1C2A24]">This is a Plus/Pro guide</p>
                    <p className="text-xs text-[#1C2A24]/50 mt-1 mb-3">Upgrade your membership to access the full guide.</p>
                    <Link to="/membership"><Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white text-sm" data-testid="upgrade-from-hub">Upgrade Now</Button></Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
